/**
 * Authenticated app shell (module rail + feature sidebar + header + body).
 * Self-contained layout chrome — do not import tenant page styles here.
 */
import styled, { css } from 'styled-components';

import { WorkspaceDropdown, WorkspaceAvatar } from '@/layout/style';
import {
    colors,
    getInsideChrome,
    getModuleRailWhiteSurfaceChrome,
    insideIsDark,
    moduleItemSelectedBg,
    moduleSidebarSelectedAccent,
    moduleSidebarSelectedFill,
    headerLight,
    rgbaFromHex,
} from '@utils/colorSystem';

/** Tenant-toolbar-style focus/hover ring for header search (dimensions stay shell defaults). */
const HEADER_SEARCH_ACCENT = '#7543DC';

/** @deprecated use `moduleItemSelectedBg` from `@utils/colorSystem` */
export const MODULE_ITEM_SELECTED_BG = moduleItemSelectedBg;
/** Icon column width (surface); total rail width adds left inset so the card sits on `insideRootBg`. */
export const MODULE_RAIL_SURFACE_WIDTH = 64;
/** Space between viewport edge and the 64px surface so gutter color is readable (not the 4px inner padding). */
export const MODULE_RAIL_INSET_LEFT = 8;
/** Inner vertical padding inside the white module rail card. */
export const MODULE_RAIL_SURFACE_PAD_Y = 14;
/** Outer vertical gap between viewport and the white module card (margin on the card). */
export const MODULE_RAIL_SURFACE_GAP_Y = 8;
/** Gap between module logo and first navigation icon. */
export const MODULE_RAIL_LOGO_TO_NAV_GAP_PX = 14;
export const MODULE_RAIL_WIDTH = MODULE_RAIL_INSET_LEFT + MODULE_RAIL_SURFACE_WIDTH;
export const FEATURE_SIDEBAR_WIDTH_DEFAULT = 220;
export const CHAT_SIDEBAR_WIDTH = 320;
/** Default content inset (px) for main column body; override per-route in `useInsideLayout` (`bodyContentPadding`). */
export const MAIN_BODY_INSET_DEFAULT_PX = 30;

/** Feature sidebar (`FeatureHeadingSection` with `$withNav`): top padding under shell chrome. */
export const FEATURE_HEADING_SECTION_PAD_TOP_PX = 24;
/** Feature sidebar: gap between heading (title) and first navigation row. */
export const FEATURE_HEADING_TO_NAV_GAP_PX = 33;

/** Fixed palette for icons/dividers on the always-white module rail card */
const MR_WHITE = getModuleRailWhiteSurfaceChrome();

export const InsideRoot = styled.div`
    width: 100%;
    max-width: 100vw;
    height: 100vh;
    overflow: hidden;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    background: ${colors.background[50]};
`;

/** Row under nothing at root — actually we use full-height row with sidebars + main */
export const InsideShellRow = styled.div`
    flex: 1;
    display: flex;
    min-height: 0;
    overflow: hidden;
`;

/** Narrow module rail: full column white; inset + outer margin frame the bordered card. */
export const ModuleRail = styled.aside`
    flex-shrink: 0;
    width: ${MODULE_RAIL_WIDTH}px;
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    box-sizing: border-box;
    background: ${colors.neutral[0]};
    padding: 0 0 0 ${MODULE_RAIL_INSET_LEFT}px;
    z-index: 2;

    @media (max-width: 1024px) {
        display: none;
    }
`;

/** White module card — outer margin top/bottom, inner padding; 1px border visible on white. */
export const ModuleRailSurface = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    max-width: ${MODULE_RAIL_SURFACE_WIDTH}px;
    box-sizing: border-box;
    margin: ${MODULE_RAIL_SURFACE_GAP_Y}px 0;
    border-radius: 12px;
    padding: ${MODULE_RAIL_SURFACE_PAD_Y}px 0 ${MODULE_RAIL_SURFACE_PAD_Y}px 4px;
    background: ${colors.neutral[0]};
    border: 1px solid ${MR_WHITE.moduleRailBorder};
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

export const ModuleRailTop = styled.div`
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding-bottom: ${MODULE_RAIL_LOGO_TO_NAV_GAP_PX}px;
`;

export const ModuleRailLogoImage = styled.img`
    width: 36px;
    height: 36px;
    object-fit: contain;
    object-position: center;
    flex-shrink: 0;
    display: block;
`;

export const ModuleRailNav = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow-y: auto;
    overflow-x: hidden;
    gap: 10px;
`;

export const ModuleRailNavButton = styled.button`
    width: 36px;
    height: 36px;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    color: ${MR_WHITE.railIdleIcon};
    transition:
        color 0.2s ease,
        background-color 0.2s ease;

    .sidebar-icon-svg {
        color: currentColor;
        width: 20px !important;
        height: 20px !important;
    }

    .anticon {
        font-size: 20px;
        width: 20px;
        height: 20px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: inherit;
    }

    .anticon svg {
        width: 20px;
        height: 20px;
    }

    &:hover:not(:disabled) {
        ${({ $active }) =>
        !$active &&
        css`
                color: ${MR_WHITE.railHoverFg};
                background: ${MR_WHITE.railHoverBg};
            `}
    }

    ${({ $active }) =>
        $active &&
        css`
            background: ${moduleSidebarSelectedFill};
            color: ${moduleSidebarSelectedAccent};
            font-weight: 600;
            .anticon {
                color: ${moduleSidebarSelectedAccent};
            }
            .sidebar-icon-svg {
                color: ${moduleSidebarSelectedAccent} !important;
            }
        `}

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

export const ModuleRailBottom = styled.div`
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding-top: 8px;
`;

export const ModuleRailIconButton = styled.button`
    width: 36px;
    height: 36px;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    border-radius: 10px;
    border: none;
    background: transparent;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: ${MR_WHITE.railIdleIcon};
    transition: background 0.2s ease, color 0.2s ease;

    .sidebar-icon-svg {
        color: currentColor;
        width: 20px !important;
        height: 20px !important;
    }

    &:hover {
        ${({ $active }) =>
        !$active &&
        css`
                background: ${MR_WHITE.railHoverBg};
                color: ${MR_WHITE.railHoverFg};
            `}
    }

    .anticon {
        font-size: 20px;
        width: 20px;
        height: 20px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }

    .anticon svg {
        width: 20px;
        height: 20px;
    }

    ${({ $active }) =>
        $active &&
        css`
            background: ${moduleSidebarSelectedFill};
            color: ${moduleSidebarSelectedAccent};
            font-weight: 600;
            .anticon {
                color: ${moduleSidebarSelectedAccent} !important;
            }
            .sidebar-icon-svg {
                color: ${moduleSidebarSelectedAccent} !important;
            }
        `}
`;

/** "More modules" trigger — icon only (label via Tooltip) */
export const ModuleRailMoreButton = styled.button`
    width: 36px;
    height: 36px;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    min-height: 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    background: transparent;
    color: ${MR_WHITE.railIdleIcon};
    transition: color 0.2s ease, background-color 0.2s ease;

    .sidebar-icon-svg {
        color: currentColor;
        width: 20px !important;
        height: 20px !important;
    }

    .anticon {
        font-size: 20px;
        width: 20px;
        height: 20px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }

    .anticon svg {
        width: 20px;
        height: 20px;
    }

    &:hover {
        ${({ $active }) =>
        !$active &&
        css`
                color: ${MR_WHITE.railHoverFg};
                background: ${MR_WHITE.railHoverBg};
            `}
    }

    ${({ $active }) =>
        $active &&
        css`
            background: ${moduleSidebarSelectedFill};
            color: ${moduleSidebarSelectedAccent};
            font-weight: 600;
            .anticon {
                color: ${moduleSidebarSelectedAccent} !important;
            }
            .sidebar-icon-svg {
                color: ${moduleSidebarSelectedAccent} !important;
            }
        `}
`;

/** Wrap module-rail avatar for online status dot */
export const ModuleRailAvatarWrap = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 40px;
    height: 40px;
`;

export const ModuleRailOnlineDot = styled.span`
    position: absolute;
    right: 1px;
    bottom: 1px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ theme }) => getInsideChrome(theme).onlineDot};
    box-sizing: border-box;
    border: 2px solid ${MR_WHITE.onlineDotRing};
    pointer-events: none;
`;

/** Feature sidebar (non-chat): account row trigger shell — opens same user menu as module rail. */
export const FeatureAccountFooterTrigger = styled.div`
    flex-shrink: 0;
    border-top: none;
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    margin: 0;
    padding: 12px 14px 24px 14px;
    box-sizing: border-box;
    cursor: pointer;
    text-align: left;
    font: inherit;
    color: inherit;
    background: transparent;

    &:hover {
        background: ${({ theme }) => getInsideChrome(theme).featureNavHoverBg};
    }
`;

export const FeatureAccountFooterInfo = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

export const FeatureAccountFooterName = styled.div`
    font-size: 13px;
    font-weight: 600;
    line-height: 1.25;
    color: ${({ theme }) => getInsideChrome(theme).featureNavFg};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const FeatureAccountFooterEmail = styled.div`
    font-size: 12px;
    line-height: 1.25;
    color: ${({ theme }) => getInsideChrome(theme).featureNavIcon};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const FeatureAccountFooterMoreIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    color: ${({ theme }) => getInsideChrome(theme).featureNavIcon};
    opacity: 0.85;
`;

/** Feature sidebar outer — full height */
export const FeatureShell = styled.aside`
    flex-shrink: 0;
    width: ${({ $width }) => $width}px;
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: ${({ theme }) => getInsideChrome(theme).featureShellBg};
    border-left: none;
    border-right: 1px solid ${({ theme }) => getInsideChrome(theme).featureShellBorder};
    transition: width 0.2s ease;
    z-index: 1;

    @media (max-width: 1024px) {
        display: none;
    }
`;

export const FeatureShellInner = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    height: 100%;
    overflow-x: hidden;
    overflow-y: hidden;
`;

/** Scroll region under the heading (project Ant Menu, etc.); menu rows align flush to the shell’s right border. */
export const FeatureSidebarBodyScroll = styled.div`
    ${({ $chat }) =>
        $chat
            ? css`
                  display: contents;
              `
            : css`
                  flex: 1;
                  min-height: 0;
                  overflow-y: auto;
                  overflow-x: hidden;
              `}

    ${({ $chat, theme }) =>
        !$chat &&
        css`
            .ant-menu.ant-menu-root.ant-menu-inline,
            .ant-menu.ant-menu-root.ant-menu-vertical {
                border-inline-end: none !important;
                border-inline-start: none !important;
                border-left: none !important;
                background: transparent !important;
            }

            .ant-menu-item,
            .ant-menu-submenu-title {
                border-radius: 10px !important;
                margin-block: 4px !important;
                margin-left: 16px !important;
                margin-right: 16px !important;
                width: calc(100% - 32px) !important;
                max-width: none !important;
                box-sizing: border-box !important;
                padding-left: 16px !important;
                padding-right: 16px !important;
            }

            .ant-menu-submenu .ant-menu-submenu-title {
                padding-right: 16px !important;
            }

            .ant-menu-item:hover:not(.ant-menu-item-selected) {
                background: #fafafa !important;
                font-weight: 500 !important;
                color: ${insideIsDark(theme) ? colors.neutral[900] : getInsideChrome(theme).featureNavFg} !important;
                .ant-menu-item-icon,
                .anticon {
                    color: ${insideIsDark(theme) ? colors.neutral[500] : getInsideChrome(theme).featureNavIcon} !important;
                }
                .ant-menu-item-icon .sidebar-icon-svg {
                    color: ${insideIsDark(theme) ? colors.neutral[500] : getInsideChrome(theme).featureNavIcon} !important;
                }
            }

            .ant-menu-item.ant-menu-item-selected {
                background: ${getInsideChrome(theme).featureNavHoverBg} !important;
                font-weight: 500 !important;
                color: ${getInsideChrome(theme).featureNavHoverFg} !important;
                .ant-menu-item-icon,
                .anticon {
                    color: ${getInsideChrome(theme).featureNavHoverIcon} !important;
                }
                .ant-menu-item-icon .sidebar-icon-svg {
                    color: ${getInsideChrome(theme).featureNavHoverIcon} !important;
                }
            }

            .ant-menu-item.ant-menu-item-selected:hover {
                background: ${getInsideChrome(theme).featureNavHoverBg} !important;
                color: ${getInsideChrome(theme).featureNavHoverFg} !important;
                .ant-menu-item-icon,
                .anticon {
                    color: ${getInsideChrome(theme).featureNavHoverIcon} !important;
                }
                .ant-menu-item-icon .sidebar-icon-svg {
                    color: ${getInsideChrome(theme).featureNavHoverIcon} !important;
                }
            }
        `}
`;

/** Title row; optional default nav — top padding + heading→nav gap per design tokens when `$withNav`. */
export const FeatureHeadingSection = styled.div`
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: ${({ $withNav }) => ($withNav ? `${FEATURE_HEADING_TO_NAV_GAP_PX}px` : '0')};
    padding: ${({ $withNav }) =>
        $withNav ? `${FEATURE_HEADING_SECTION_PAD_TOP_PX}px 16px 12px 16px` : '20px 16px 30px'};
`;

export const FeatureHeading = styled.div`
    margin: 0;
    flex-shrink: 0;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.25;
    color: ${({ theme }) => getInsideChrome(theme).featureHeading};
    font-family: var(--font-family-geist, inherit);
`;

/** Default feature sidebar links — same hover/selected affordance as module rail (`ModuleRailNavButton`). */
export const FeatureSidebarNav = styled.nav`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    margin: 0;
    padding: 0;
`;

export const FeatureSidebarNavItem = styled.button`
    width: 100%;
    margin: 0;
    padding: 8px 16px;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    background: transparent;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.2;
    text-align: left;
    font-family: var(--font-family-geist, inherit);
    color: ${({ theme }) => getInsideChrome(theme).featureNavFg};
    transition:
        color 0.2s ease,
        background-color 0.2s ease;

    .feature-nav-icon {
        flex-shrink: 0;
        width: 20px;
        height: 20px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: ${({ theme }) => getInsideChrome(theme).featureNavIcon};
        transition: color 0.2s ease;
    }

    .feature-nav-icon .anticon {
        font-size: 20px;
        width: 20px;
        height: 20px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: inherit;
    }

    .feature-nav-icon .anticon svg {
        width: 20px;
        height: 20px;
    }

    .feature-nav-icon .sidebar-icon-svg {
        width: 20px !important;
        height: 20px !important;
        color: inherit;
    }

    .feature-nav-label {
        flex: 1;
        min-width: 0;
    }

    &:hover:not(:disabled) {
        ${({ $active, theme }) =>
        !$active &&
        css`
                background: #fafafa;
                font-weight: 500;
                ${insideIsDark(theme)
                ? css`
                          color: ${colors.neutral[900]};
                          .feature-nav-icon {
                              color: ${colors.neutral[500]};
                          }
                      `
                : css`
                          color: ${getInsideChrome(theme).featureNavFg};
                          .feature-nav-icon {
                              color: ${getInsideChrome(theme).featureNavIcon};
                          }
                      `}
            `}
    }

    ${({ $active, theme }) =>
        $active &&
        css`
            background: ${getInsideChrome(theme).featureNavHoverBg};
            color: ${getInsideChrome(theme).featureNavHoverFg};
            font-weight: 500;
            .feature-nav-icon {
                color: ${getInsideChrome(theme).featureNavHoverIcon};
            }
        `}

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

/** Main column: header + scroll body */
export const MainColumn = styled.div`
    flex: 1;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
    isolation: isolate;
    ${({ theme }) =>
        insideIsDark(theme)
            ? css`
                  background: ${theme.colors.background.default};
              `
            : css`
                   background-color: transparent;
                  &::before {
                      content: '';
                      position: absolute;
                      left: 0;
                      right: 0;
                      top: 0;
                      height: ${headerLight.gradientHeightPx}px;
                      z-index: 0;
                      pointer-events: none;
                      background-image: linear-gradient(
                          90deg,
                          ${headerLight.gradientLeft} 0%,
                          ${headerLight.gradientRight} 100%
                      );
                      background-size: 100% 100%;
                      background-repeat: no-repeat;
                      background-position: top left;
                      -webkit-mask-image: linear-gradient(
                          to bottom,
                          #000 0%,
                          #000 ${headerLight.gradientFadeOpaqueStopPct}%,
                          transparent 100%
                      );
                      -webkit-mask-size: 100% 100%;
                      -webkit-mask-repeat: no-repeat;
                      -webkit-mask-position: 0 0;
                      mask-image: linear-gradient(
                          to bottom,
                          #000 0%,
                          #000 ${headerLight.gradientFadeOpaqueStopPct}%,
                          transparent 100%
                      );
                      mask-size: 100% 100%;
                      mask-repeat: no-repeat;
                      mask-position: 0 0;
                  }
              `}
`;

/** Wraps main-column header (shares continuous background with body via `MainColumn`). */
export const HeaderChrome = styled.div`
    flex-shrink: 0;
    position: relative;
    z-index: 2;
`;

export const AppHeader = styled.header`
    flex-shrink: 0;
    /* Height follows the control row (e.g. 32px search); avoid min-height dead band under the bar + body padding reading as >30px gap. */
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: wrap;
    row-gap: 10px;
    column-gap: 16px;
    padding: 12px 24px;
    box-sizing: border-box;
    position: relative;
    z-index: 2;
    margin: 0;
    background: ${({ theme }) =>
        insideIsDark(theme) ? getInsideChrome(theme).appHeaderBg : 'transparent'};
`;

export const HeaderLeftCluster = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1 1 160px;
    min-width: 0;
`;

export const HeaderRightCluster = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
    margin-left: auto;

    .ant-dropdown-trigger {
        display: inline-flex;
        align-items: center;
        margin: 0;
        line-height: 0;
    }
`;

/** Workspace trigger — 32px tall to match search control; 8px radius; avatar fills row. */
export const HeaderWorkspaceDropdown = styled(WorkspaceDropdown)`
    border: none;
    box-shadow: none;
    height: 32px;
    min-height: 32px;
    max-height: 32px;
    padding: 0;
    border-radius: 8px;
    width: 32px;
    min-width: 32px;
    max-width: 32px;
    justify-content: center;
    align-items: center;
    gap: 0;
    box-sizing: border-box;
    line-height: 0;
    background: transparent;

    &:hover {
        background: ${({ theme }) =>
        insideIsDark(theme) ? rgbaFromHex(colors.neutral[0], 0.08) : rgbaFromHex(colors.neutral[900], 0.06)};
    }

    ${WorkspaceAvatar} {
        width: 32px;
        height: 32px;
        border-radius: 8px;
        font-size: 0.75rem;
        border: none;
        box-shadow: none;
    }

    .anticon {
        display: none;
    }
`;

export const BreadcrumbRow = styled.nav`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    flex: 1;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.25;
    font-family: var(--font-family-geist, inherit);
    color: ${({ theme }) => getInsideChrome(theme).breadcrumbMuted};
`;

export const CrumbLink = styled.button`
    border: none;
    background: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.25;
    font-family: var(--font-family-geist, inherit);
    color: ${({ theme }) => getInsideChrome(theme).breadcrumbMuted};

    &:hover {
        color: ${({ theme }) => getInsideChrome(theme).crumbCurrent};
    }

    /* Optical vertical center with 14px breadcrumb labels (slight lift vs cap height). */
    &:has(.sidebar-icon-svg) {
        padding-bottom: 3px;
    }

    .sidebar-icon-svg {
        color: currentColor;
        width: 15px !important;
        height: 15px !important;
    }
`;

export const CrumbSep = styled.span`
    color: ${({ theme }) => getInsideChrome(theme).breadcrumbMuted};
    font-size: 14px;
    font-weight: 500;
    user-select: none;
    cursor: default;
`;

export const CrumbInactiveText = styled.span`
    font-size: 14px;
    font-weight: 500;
    line-height: 1.25;
    font-family: var(--font-family-geist, inherit);
    color: ${({ theme }) => getInsideChrome(theme).breadcrumbMuted};
    cursor: default;
`;

export const CrumbCurrent = styled.span`
    font-size: 14px;
    font-weight: 500;
    line-height: 1.25;
    font-family: var(--font-family-geist, inherit);
    color: ${({ theme }) => getInsideChrome(theme).crumbCurrent};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
    margin-left: auto;
`;

export const HeaderSearchWrap = styled.div`
    flex: 0 0 280px;
    width: 280px;
    min-width: 280px;
    max-width: 280px;
    margin: 0;
    line-height: 0;

    .ant-input-affix-wrapper {
        margin: 0;
        border-radius: 10px;
        height: 32px;
        min-height: 32px;
        max-height: 32px;
        box-sizing: border-box;
        border-width: 1px;
        border-style: solid;
        outline: none;
        box-shadow: none;
        transition:
            border-color 0.2s ease,
            background-color 0.2s ease;

        background: ${({ theme }) => getInsideChrome(theme).searchBg};
        border-color: ${({ theme }) => getInsideChrome(theme).searchBorder};
        color: ${({ theme }) => (insideIsDark(theme) ? '#fff' : '#171717')};

        .anticon {
            font-size: 14px;
            color: ${({ theme }) => (insideIsDark(theme) ? 'rgba(255,255,255,0.5)' : '#8c8c8c')};
            transition: color 0.2s ease;
        }

        &:hover:not(.ant-input-affix-wrapper-disabled) {
            box-shadow: none;

            ${({ theme }) => {
        const dark = insideIsDark(theme);
        if (dark) {
            return css`
                        border-color: rgba(255, 255, 255, 0.24);
                        background: #222;
                    `;
        }
        return css`
                    border-color: ${rgbaFromHex(HEADER_SEARCH_ACCENT, 0.48)};
                    background: #f6f2ff;
                `;
    }}
        }

        &:hover:not(.ant-input-affix-wrapper-disabled) .anticon {
            color: ${({ theme }) => (insideIsDark(theme) ? 'rgba(255,255,255,0.9)' : HEADER_SEARCH_ACCENT)};
        }

        &.ant-input-affix-wrapper-focused,
        &:focus-within {
            box-shadow: none;
            outline: none;

            ${({ theme }) => {
        const dark = insideIsDark(theme);
        if (dark) {
            return css`
                        border-color: rgba(255, 255, 255, 0.38);
                        background: #1c1c1c;
                    `;
        }
        return css`
                    border-color: ${HEADER_SEARCH_ACCENT};
                    background: ${getInsideChrome(theme).searchBg};
                `;
    }}
        }

        &.ant-input-affix-wrapper-focused .anticon,
        &:focus-within .anticon {
            color: ${({ theme }) => (insideIsDark(theme) ? 'rgba(255,255,255,0.9)' : HEADER_SEARCH_ACCENT)};
        }
    }

    .ant-input {
        margin: 0;
        font-size: 14px;
        font-family: var(--font-family-geist), system-ui, sans-serif;
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        color: inherit;

        &::placeholder {
            color: ${({ theme }) => (insideIsDark(theme) ? 'rgba(255,255,255,0.45)' : '#8c8c8c')};
        }

        &:focus,
        &:focus-visible {
            box-shadow: none !important;
            outline: none !important;
        }
    }

    .ant-input-clear-icon {
        color: ${({ theme }) => (insideIsDark(theme) ? 'rgba(255,255,255,0.45)' : '#8c8c8c')};
    }
`;

export const SearchHotkeyHint = styled.span`
    font-size: 0.6875rem;
    color: ${({ theme }) => getInsideChrome(theme).searchHotkey};
    letter-spacing: 0.02em;
    margin-left: 4px;
`;

export const GhostHeaderButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 36px;
    padding: 0 14px;
    border-radius: 10px;
    border: 1px solid ${({ theme }) => getInsideChrome(theme).ghostBtnBorder};
    background: transparent;
    font-size: 0.875rem;
    font-weight: 500;
    color: ${({ theme }) => getInsideChrome(theme).ghostBtnFg};
    cursor: pointer;
    transition: background 0.2s ease, border-color 0.2s ease;

    &:hover {
        background: ${({ theme }) => getInsideChrome(theme).ghostBtnHoverBg};
        border-color: ${({ theme }) => getInsideChrome(theme).ghostBtnHoverBorder};
    }

    .anticon {
        font-size: 16px;
    }
`;

export const HeaderIconButton = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease;
    color: ${({ theme }) => getInsideChrome(theme).headerIconDefault};

    ${({ $active, theme }) =>
        $active
            ? `
        background: ${getInsideChrome(theme).headerIconActiveBg};
        color: ${getInsideChrome(theme).headerIconActiveFg};
        .anticon { color: ${getInsideChrome(theme).headerIconActiveFg} !important; }
    `
            : ''}

    &:hover {
        ${({ $active, theme }) =>
        !$active &&
        css`
                background: ${getInsideChrome(theme).headerIconHoverBg};
                color: ${getInsideChrome(theme).headerIconHoverFg};
            `}
    }

    .anticon {
        font-size: 18px;
    }
`;

export const AppBody = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
    z-index: 1;
    box-sizing: border-box;
`;

/**
 * Padded content inside the main-column scroll viewport — keeps insets off the scrollbar
 * so the vertical bar sits flush with the main column edge (`InsideBodyScroll` has no horizontal padding).
 */
export const InsideBodyScrollInner = styled.div`
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    padding-top: ${({ $pt }) => Number($pt)}px;
    padding-right: ${({ $pr }) => Number($pr)}px;
    padding-bottom: ${({ $pb }) => Number($pb)}px;
    padding-left: ${({ $pl }) => Number($pl)}px;

    ${({ $kanbanLock }) =>
        $kanbanLock
            ? css`
                  flex: 1;
                  min-height: 0;
                  display: flex;
                  flex-direction: column;
              `
            : ''}

    /* Keep shell top inset as the only top gap (e.g. 30px); flex items’ margins would stack on padding. */
    & > *:first-child {
        margin-top: 0;
        margin-block-start: 0;
    }
`;

/**
 * Main column scroll surface — scrollbar uses full width of the column (no padding on this layer).
 * Content insets live on `InsideBodyScrollInner`.
 */
export const InsideBodyScroll = styled.div.attrs({
    'data-inside-body-scroll': 'true',
    className: 'inside-body-scroll',
})`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow-y: ${({ $lockHeight }) => ($lockHeight ? 'hidden' : 'auto')};
    overflow-x: hidden;
    box-sizing: border-box;
    padding: 0;

    ${({ $lockHeight, theme }) =>
        !$lockHeight &&
        `
    &::-webkit-scrollbar { width: 8px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
        background: ${getInsideChrome(theme).scrollbarThumb};
        border-radius: 4px;
        &:hover { background: ${getInsideChrome(theme).scrollbarThumbHover}; }
    }
    scrollbar-width: thin;
    scrollbar-color: ${getInsideChrome(theme).scrollbarThumb} transparent;
    `}

    ${({ $lockHeight, theme }) =>
        $lockHeight &&
        `
        scrollbar-width: none;
        -ms-overflow-style: none;
        &::-webkit-scrollbar { width: 0; height: 0; }
        background: ${insideIsDark(theme) ? getInsideChrome(theme).pageScrollLockedBg : 'transparent'};
    `}
`;
